import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Send, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { CLINIC, SPECIALTY_OPTIONS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", specialty: "", preferred_date: "", message: "", gdpr_consent: false });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Te rugăm completează numele și telefonul.");
      return;
    }
    if (!form.gdpr_consent) {
      toast.error("Este necesar acordul GDPR pentru a trimite mesajul.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mulțumim! Te vom contacta în cel mai scurt timp pentru programare.");
      setForm({ name: "", phone: "", email: "", specialty: "", preferred_date: "", message: "", gdpr_consent: false });
    } catch (err) {
      toast.error("A apărut o eroare. Sună-ne direct la " + CLINIC.phone);
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-[#A6823B] focus:ring-4 focus:ring-[#A6823B]/10 outline-none text-slate-800 transition-all";

  return (
    <section id="contact" className="py-20 lg:py-28 bg-[#FBF7F0]">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
        <Reveal>
          <span className="text-xs font-semibold uppercase tracking-widest text-[#A6823B]">Contact</span>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mt-3">
            Programează-te — de restul ne ocupăm noi
          </h2>
          <p className="text-slate-600 mt-4 text-lg">
            Cel mai rafinat mod de a începe este un simplu telefon. Sau lasă-ne datele și te contactăm noi.
          </p>
          <a href={CLINIC.phoneRaw}
            className="mt-8 inline-flex items-center gap-3 bg-[#A6823B] hover:bg-[#8A6B2E] text-white font-semibold text-lg px-7 py-4 rounded-full shadow-xl shadow-[#A6823B]/25 transition-all hover:-translate-y-0.5">
            <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center pulse-ring">
              <Phone className="w-4 h-4" />
            </span>
            {CLINIC.phone}
          </a>
          <div className="mt-8 p-5 rounded-2xl bg-white border border-slate-200">
            <p className="text-sm text-slate-500">
              Datele tale sunt procesate confidențial, conform Regulamentului GDPR (UE) 2016/679.
              Nu le folosim în scopuri de marketing fără acordul tău.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-2xl border border-slate-200 p-6 lg:p-8 space-y-4 shadow-lg shadow-slate-200/50">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nume complet *</label>
                <input data-testid="contact-form-name" className={field} value={form.name}
                  onChange={(e) => update("name", e.target.value)} placeholder="Ion Popescu" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Telefon *</label>
                <input data-testid="contact-form-phone" className={field} value={form.phone}
                  onChange={(e) => update("phone", e.target.value)} placeholder="07xx xxx xxx" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
                <input data-testid="contact-form-email" type="email" className={field} value={form.email}
                  onChange={(e) => update("email", e.target.value)} placeholder="email@exemplu.ro" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Specialitate</label>
                <select data-testid="contact-form-specialty-select" className={field} value={form.specialty}
                  onChange={(e) => update("specialty", e.target.value)}>
                  <option value="">Alege...</option>
                  {SPECIALTY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Dată preferată</label>
              <input data-testid="contact-form-date" type="date" className={field} value={form.preferred_date}
                onChange={(e) => update("preferred_date", e.target.value)} />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Mesaj</label>
              <textarea data-testid="contact-form-message" rows={3} className={field} value={form.message}
                onChange={(e) => update("message", e.target.value)} placeholder="Cum te putem ajuta?" />
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="contact-form-gdpr-checkbox" checked={form.gdpr_consent}
                onChange={(e) => update("gdpr_consent", e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#A6823B]" />
              <span className="text-sm text-slate-500">
                Sunt de acord cu prelucrarea datelor mele personale în scopul programării, conform GDPR. *
              </span>
            </label>

            <button type="submit" disabled={loading} data-testid="contact-form-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#A6823B] hover:bg-[#8A6B2E] disabled:opacity-60 text-white font-semibold px-5 py-3.5 rounded-full transition-colors">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {loading ? "Se trimite..." : "Trimite cererea de programare"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
